import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Award,
    Clock,
    Building2,
    ShieldCheck,
    Phone,
    MessageCircle,
    Check,
    CheckCircle2,
    Sun,
    Wind,
    Calendar,
    Ruler,
    Wrench,
    Droplets,
    Palette,
    Truck,
    Car,
    GraduationCap,
    Factory,
    Home as HomeIcon,
    Trophy,
    Hospital,
    Plane,
    Hotel,
    Tractor,
    Warehouse,
    ArrowRight,
    Download,
    Menu,
    ChevronDown,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import { CrossSiteNavMobile, SiteUtilityBar } from "@/components/CrossSiteNav";
import StickyMobileCTABar from "@/components/StickyMobileCTABar";
import BackToTop from "@/components/BackToTop";
import Lightbox from "@/components/Lightbox";
import HeroQuickForm from "@/components/HeroQuickForm";
import InlineCTABanner from "@/components/InlineCTABanner";
import Breadcrumbs from "@/components/Breadcrumbs";
import VideoSection from "@/components/VideoSection";
import YouTubeShortsRow from "@/components/YouTubeShortsRow";
import videos from "@/data/videos.json";
import { applySEO, orgSchema, localBusinessSchema, breadcrumbSchema, productSchema, faqSchema, videoObjectSchema } from "@/lib/seo";
import { submitLead, submitCatalogLead, trackEvent } from "@/lib/api";

const TEL = "+52 81 8989 9822";
const TEL_RAW = "528189899822";
const WA_RAW = "528116289267";
const WA_HERO =
    "https://wa.me/" +
    WA_RAW +
    "?text=" +
    encodeURIComponent(
        "Hola, quiero cotizar gratis un techo FlexShade para mi proyecto.",
    );
const WA_FLOATING_MSG =
    "Hola, me interesa cotizar un techo FlexShade. ¿Pueden orientarme?";

const HERO_IMG =
    "https://static.prod-images.emergentagent.com/jobs/48bc66d4-e137-48bf-811d-bee5c078c182/images/39768112c37603bbefa107228ce3fc9ed0bf44b7269579c36900d5b4f97d6eb0.jpeg";

const MODELS = [
    {
        id: "walk",
        title: "FlexShade Walk",
        subtitle: "Plano elevado",
        img: "/images/models/walk.png",
        desc: "Diseño plano y elevado. Ideal para pasillos, accesos peatonales y zonas de espera.",
        specs: ["Altura 3m+", "Para andadores", "Cubierta continua"],
        popular: false,
    },
    {
        id: "curve",
        title: "FlexShade Curve",
        subtitle: "Arco curvo clásico",
        img: "/images/models/curve.png",
        desc: "Nuestro modelo más popular. Arco aerodinámico para estacionamientos grandes.",
        specs: ["Arco aerodinámico", "Drenaje natural", "Estética premium"],
        popular: true,
    },
    {
        id: "curve-corto",
        title: "FlexShade Curve Corto",
        subtitle: "Arco compacto",
        img: "/images/models/curve_corto.png",
        desc: "Versión compacta del Curve. Perfecto para espacios reducidos y residenciales.",
        specs: ["Perfil compacto", "Económico", "Fácil instalación"],
        popular: false,
    },
    {
        id: "sun",
        title: "FlexShade Sun",
        subtitle: "Pendiente inclinada",
        img: "/images/models/sun.png",
        desc: "Pendiente unilateral. Ideal para drenaje pluvial óptimo y zonas con sol direccional.",
        specs: ["Drenaje óptimo", "Para terrazas", "Ángulos a medida"],
        popular: false,
    },
    {
        id: "back-to-back",
        title: "FlexShade Back to Back",
        subtitle: "Doble fila espalda con espalda",
        img: "/images/models/back_to_back.png",
        desc: "Dos curves enfrentados. Máxima cobertura para doble fila de cajones de estacionamiento.",
        specs: ["Doble fila simétrica", "Máxima eficiencia", "Ideal para estacionamientos"],
        popular: false,
    },
];

const SPECS = [
    { Icon: Sun, value: "95%", label: "Bloqueo UV" },
    { Icon: Wind, value: "70 km/hr", label: "Resistencia al viento" },
    { Icon: Calendar, value: "20 años", label: "Vida útil garantizada" },
    { Icon: Ruler, value: "Cualquier tamaño", label: "Desde 1 cajón" },
    { Icon: Wrench, value: "100%", label: "Atornillable" },
    { Icon: Droplets, value: "Drenaje", label: "Natural inclinado" },
    { Icon: Palette, value: "24", label: "Colores disponibles" },
    { Icon: Truck, value: "30 ciudades", label: "Cobertura nacional" },
];

const NICHES = [
    { Icon: Car, title: "Estacionamientos", desc: "Centros comerciales, oficinas, hospitales", tipo: "Estacionamiento" },
    { Icon: GraduationCap, title: "Escuelas y colegios", desc: "Patios, accesos, comedores", tipo: "Escuela" },
    { Icon: Factory, title: "Industria", desc: "Áreas de carga y descarga", tipo: "Industria" },
    { Icon: HomeIcon, title: "Residencial", desc: "Terrazas, patios, cocheras", tipo: "Residencial" },
    { Icon: Trophy, title: "Deportivo", desc: "Canchas, gradas, zonas de juego", tipo: "Área deportiva" },
    { Icon: Building2, title: "Comercial", desc: "Plazas, terrazas de restaurantes", tipo: "Comercial" },
    { Icon: Hospital, title: "Hospitales", desc: "Accesos, ambulancias, áreas externas", tipo: "Comercial" },
    { Icon: Plane, title: "Aeropuertos", desc: "Zonas de espera y carga", tipo: "Comercial" },
    { Icon: Hotel, title: "Hoteles", desc: "Albercas, accesos, valet parking", tipo: "Comercial" },
    { Icon: Tractor, title: "Agrícola", desc: "Granjas, invernaderos, áreas de trabajo", tipo: "Otro" },
    { Icon: Warehouse, title: "Logística", desc: "Patios de maniobras y carga", tipo: "Industria" },
    { Icon: Truck, title: "Transportes", desc: "Bases y patios de transportes", tipo: "Industria" },
];

const GALLERY = [
    { img: "https://static.prod-images.emergentagent.com/jobs/48bc66d4-e137-48bf-811d-bee5c078c182/images/e58e17e4949a4739e701bcfbbb9f59bccb90e8f56a88b0f10e089059b7e11238.jpeg", title: "Estacionamiento corporativo", city: "Monterrey" },
    { img: "https://static.prod-images.emergentagent.com/jobs/48bc66d4-e137-48bf-811d-bee5c078c182/images/689154a9e199c8fdd9a966461b3697ce1d0f26221866aeccbf51f7ce4c9722d2.jpeg", title: "Patio industrial Flamatec", city: "Saltillo" },
    { img: "https://static.prod-images.emergentagent.com/jobs/48bc66d4-e137-48bf-811d-bee5c078c182/images/7955f8ca37334ad15bb681274c15e4229c09fc155ff4a088f7cdb4d89c9631ce.jpeg", title: "Gradas escolares", city: "Monterrey" },
    { img: "https://static.prod-images.emergentagent.com/jobs/48bc66d4-e137-48bf-811d-bee5c078c182/images/ea3ab7e2e82917cfc6ad545ce4da48014c3a4da65ee8eac6827a82c9d518c1d3.jpeg", title: "Patio escolar", city: "Saltillo" },
    { img: "https://static.prod-images.emergentagent.com/jobs/48bc66d4-e137-48bf-811d-bee5c078c182/images/b628e71bcfd23dc7910bd3bcfc33681806088b54756d9084a5121376c23be19a.jpeg", title: "Bodega industrial", city: "Apodaca" },
    { img: "https://static.prod-images.emergentagent.com/jobs/48bc66d4-e137-48bf-811d-bee5c078c182/images/4e8ecf31140220c752c3e0140dffca4e30655ddd7b30a018f33c640a6a019a2e.jpeg", title: "Plaza comercial", city: "Monterrey" },
];

const COLORS = [
    { name: "Negro", hex: "#0A0A0A" },
    { name: "Desert Sun", hex: "#C9A87C" },
    { name: "Aquatic Blue", hex: "#1F8FBA" },
    { name: "Steel Grey", hex: "#6B7280" },
    { name: "Brunswick Green", hex: "#1F3D2B" },
];

const MEMBRANE_BRANDS = [
    "Dickson",
    "Naizil",
    "Polyfab",
    "Sioen",
    "Serge Ferrari",
    "Waterproof",
    "Verseidag",
];

const TIPOS_ESPACIO = ["Estacionamiento", "Industria", "Escuela", "Residencial", "Área deportiva", "Comercial", "Otro"];
const M2_OPTIONS = ["Menos de 50 m²", "50-200 m²", "200-500 m²", "Más de 500 m²"];
const CIUDADES = ["Monterrey", "Saltillo", "Torreón", "Chihuahua", "Tamaulipas", "Bajío", "CDMX", "Guadalajara", "Otra"];

const FAQS = [
    { q: "¿Cuánto cuesta un techo FlexShade?", a: "El precio depende del modelo, tamaño y especificaciones. Una cotización promedio para un estacionamiento de 10 cajones está entre $80,000 y $180,000 MXN. Cotiza gratis para tu caso específico." },
    { q: "¿Cuánto tarda la instalación?", a: "Entre 5 y 15 días naturales según el tamaño. Al ser modular y atornillable, evitamos los tiempos de soldadura." },
    { q: "¿Necesito permisos de construcción?", a: "Por su naturaleza modular y atornillable, en la mayoría de los municipios NO requiere permisos. Te orientamos en tu caso particular." },
    { q: "¿Qué garantía incluye?", a: "10 años de garantía de fábrica por escrito. Vida útil hasta 20 años con malla importada premium. Premio IFAI 2021 respalda nuestra calidad." },
    { q: "¿Hacen instalación fuera de Monterrey?", a: "Sí, tenemos cobertura en más de 30 ciudades de México: Saltillo, Torreón, Chihuahua, Tamaulipas, Bajío, CDMX, Guadalajara y más." },
    { q: "¿Qué pasa con el viento o el granizo?", a: "Nuestros sistemas están calculados para resistencias hasta 70 km/hr. La malla sombra es altamente resistente y tiene drenaje natural." },
    { q: "¿Puedo cambiar el color después?", a: "Sí, la malla sombra es reemplazable. Si en años necesitas otro color o renovarla, solo cambias la malla manteniendo la estructura." },
    { q: "¿Hacen financiamiento?", a: "Sí, manejamos esquemas de pago con anticipo y saldo contra entrega. También podemos facturar a empresas." },
];

// ---------- Sub-componentes ----------

function NavbarFlex({ onCotizar }) {
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const navigate = useNavigate();
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const scrollTo = (id) => {
        setMobileOpen(false);
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <header
            data-testid="flexshade-navbar"
            className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${scrolled ? "bg-black/95 backdrop-blur-md border-b border-white/10" : "bg-transparent"}`}
        >
            <SiteUtilityBar active="flexshade" accent="#E86A1F" testId="flexshade-utility-bar" />
            <div className="container-eclipse flex items-center justify-between h-20 gap-4">
                <Link to="/flexshade" data-testid="flexshade-navbar-logo" className="flex items-center gap-3 leading-none flex-shrink-0">
                    <img src="/brand/flexshade_logo.png" alt="FlexShade by Eclipse Cubiertas — Techos de Malla Sombra Modulares" className="h-9 sm:h-10 w-auto object-contain" />
                </Link>

                <nav className="hidden lg:flex items-center gap-5 xl:gap-6">
                    <Link to="/" data-testid="flex-nav-inicio" className="text-xs xl:text-sm font-display font-semibold tracking-wider text-white hover:text-eclipse-cta transition-colors">INICIO</Link>

                    <DropdownMenu>
                        <DropdownMenuTrigger data-testid="flex-nav-modelos-trigger" className="flex items-center gap-1 text-xs xl:text-sm font-display font-semibold tracking-wider text-white hover:text-eclipse-cta transition-colors">
                            MODELOS <ChevronDown className="w-4 h-4" />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="bg-black/95 backdrop-blur-md border-white/10 w-56">
                            {MODELS.map((m) => (
                                <DropdownMenuItem key={m.id} data-testid={`flex-nav-modelo-${m.id}`} onClick={() => scrollTo(`modelo-${m.id}`)} className="cursor-pointer font-body text-white hover:text-eclipse-cta focus:text-eclipse-cta focus:bg-white/5">
                                    <div className="flex flex-col">
                                        <span className="font-display font-bold text-sm leading-tight">{m.title.replace("FlexShade ", "")}</span>
                                        <span className="text-[11px] text-white/60">{m.subtitle}</span>
                                    </div>
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>

                    <button onClick={() => scrollTo("especificaciones")} data-testid="flex-nav-specs" className="text-xs xl:text-sm font-display font-semibold tracking-wider text-white hover:text-eclipse-cta transition-colors">ESPECIFICACIONES</button>
                    <button onClick={() => scrollTo("proyectos")} data-testid="flex-nav-proyectos" className="text-xs xl:text-sm font-display font-semibold tracking-wider text-white hover:text-eclipse-cta transition-colors">PROYECTOS</button>
                    <button onClick={() => scrollTo("materiales")} data-testid="flex-nav-materiales" className="text-xs xl:text-sm font-display font-semibold tracking-wider text-white hover:text-eclipse-cta transition-colors">MATERIALES</button>
                    <button onClick={() => scrollTo("faq")} data-testid="flex-nav-faq" className="text-xs xl:text-sm font-display font-semibold tracking-wider text-white hover:text-eclipse-cta transition-colors">FAQ</button>
                    <button onClick={() => scrollTo("cotizar")} data-testid="flex-nav-contacto" className="text-xs xl:text-sm font-display font-semibold tracking-wider text-white hover:text-eclipse-cta transition-colors">CONTACTO</button>
                </nav>

                <div className="flex items-center gap-3">
                    <a
                        href={`tel:${TEL_RAW}`}
                        data-testid="flexshade-navbar-phone"
                        className="hidden xl:inline-flex items-center gap-2 text-xs font-display font-semibold text-white hover:text-eclipse-cta transition-colors"
                    >
                        <Phone className="w-4 h-4" />
                        {TEL}
                    </a>
                    <button
                        onClick={onCotizar}
                        data-testid="flexshade-navbar-cta"
                        className="btn-eclipse-cta hidden sm:inline-flex items-center gap-2 px-4 py-2.5 rounded-sm text-xs uppercase tracking-wider"
                    >
                        Cotiza
                    </button>
                    <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                        <SheetTrigger asChild data-testid="flex-nav-mobile-trigger">
                            <button aria-label="Abrir menú" className="lg:hidden p-2 rounded-md text-white">
                                <Menu className="w-6 h-6" />
                            </button>
                        </SheetTrigger>
                        <SheetContent side="right" className="bg-white w-80 p-0 overflow-y-auto">
                            <SheetHeader className="px-6 pt-6 pb-4 border-b border-neutral-200 bg-eclipse-primary flex flex-row items-center">
                                <SheetTitle asChild>
                                    <div className="flex items-center gap-3">
                                        <img src="/brand/eclipse_logo.png" alt="Eclipse" className="h-9 w-auto object-contain" />
                                        <img src="/brand/flexshade_logo.png" alt="FlexShade" className="h-8 w-auto object-contain" />
                                    </div>
                                </SheetTitle>
                                <SheetDescription className="sr-only">Menú FlexShade</SheetDescription>
                            </SheetHeader>
                            <div className="px-6 pt-6 pb-2">
                                <CrossSiteNavMobile active="flexshade" onNavigate={() => setMobileOpen(false)} />
                            </div>
                            <nav className="flex flex-col p-6 pt-2 gap-1">
                                <MobileItemFlex label="Inicio" onClick={() => { setMobileOpen(false); navigate("/"); }} testId="flex-mobile-nav-inicio" />
                                <MobileItemFlex label="Modelos" onClick={() => scrollTo("modelos")} testId="flex-mobile-nav-modelos" />
                                <MobileItemFlex label="Especificaciones" onClick={() => scrollTo("especificaciones")} testId="flex-mobile-nav-specs" />
                                <MobileItemFlex label="Proyectos" onClick={() => scrollTo("proyectos")} testId="flex-mobile-nav-proyectos" />
                                <MobileItemFlex label="Materiales" onClick={() => scrollTo("materiales")} testId="flex-mobile-nav-materiales" />
                                <MobileItemFlex label="FAQ" onClick={() => scrollTo("faq")} testId="flex-mobile-nav-faq" />
                                <MobileItemFlex label="Contacto" onClick={() => scrollTo("cotizar")} testId="flex-mobile-nav-contacto" />
                                <a href={`tel:${TEL_RAW}`} className="mt-2 inline-flex items-center gap-2 py-3 px-2 font-display font-semibold text-eclipse-primary hover:text-eclipse-cta">
                                    <Phone className="w-4 h-4" /> {TEL}
                                </a>
                                <button onClick={() => { setMobileOpen(false); onCotizar(); }} data-testid="flex-mobile-nav-cta" className="btn-eclipse-cta mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-sm text-sm uppercase tracking-wider">
                                    Cotizar ahora
                                </button>
                            </nav>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
}

function MobileItemFlex({ label, onClick, testId }) {
    return (
        <button onClick={onClick} data-testid={testId} className="w-full text-left py-3 px-2 font-display font-semibold text-base text-eclipse-primary hover:text-eclipse-cta hover:bg-neutral-50 rounded-md transition-colors">
            {label}
        </button>
    );
}

function HeroFlex({ onCotizar }) {
    return (
        <section data-testid="flexshade-hero" className="relative min-h-[100vh] flex items-end overflow-hidden">
            <div className="absolute inset-0 bg-cover bg-center z-0" style={{ backgroundImage: `url(${HERO_IMG})` }} aria-hidden />
            <div className="absolute inset-0 z-10" style={{ background: "linear-gradient(to top, rgba(10,10,10,0.92) 0%, rgba(10,10,10,0.6) 50%, rgba(10,10,10,0.25) 100%)" }} aria-hidden />
            <div className="relative z-20 container-eclipse pt-32 pb-16 sm:pb-24 w-full">
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-10 lg:gap-12 items-end">
                    <div className="max-w-4xl">
                        <p className="font-display font-bold text-xs sm:text-sm tracking-[0.3em] text-eclipse-cta mb-5 fade-up">PIONEROS EN MÉXICO · +10,000 TECHOS INSTALADOS</p>
                        <h1 data-testid="flexshade-hero-title" className="font-display font-black text-white text-[36px] leading-[1.05] sm:text-5xl lg:text-[60px] lg:leading-[1.05] tracking-tight mb-6 fade-up" style={{ animationDelay: "0.1s" }}>
                            Techos de <span className="text-eclipse-cta">Malla Sombra</span><br />Modulares para tu Estacionamiento
                        </h1>
                        <p className="text-white/90 text-base sm:text-lg max-w-2xl leading-relaxed mb-10 fade-up" style={{ animationDelay: "0.2s" }}>
                            Sistema prefabricado. Sin permiso. Sin soldadura. Instalación en días. +10,000 techos instalados en México.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 fade-up" style={{ animationDelay: "0.3s" }}>
                            <a href={WA_HERO} target="_blank" rel="noopener noreferrer" data-testid="flexshade-hero-whatsapp" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-sm font-display font-bold text-sm uppercase tracking-wider bg-[#25D366] hover:bg-[#1ebd5b] text-white transition-all hover:-translate-y-0.5">
                                <MessageCircle className="w-5 h-5" />
                                Cotizar por WhatsApp
                            </a>
                            <button onClick={onCotizar} data-testid="flexshade-hero-cta" className="btn-eclipse-cta px-7 py-4 rounded-sm text-sm tracking-wider uppercase">
                                Cotizar en línea →
                            </button>
                        </div>
                    </div>
                    <div className="fade-up" style={{ animationDelay: "0.35s" }}>
                        <HeroQuickForm sitio="flexshade" producto="flexshade" accentLabel="Cotización gratis 24h" testId="flexshade-hero-quickform" />
                    </div>
                </div>
                <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-6 pt-8 border-t border-white/15 fade-up" style={{ animationDelay: "0.4s" }}>
                    {[{ Icon: Award, t: "Premio IFAI 2021" }, { Icon: Clock, t: "+20 años" }, { Icon: Building2, t: "+10,000 techos" }, { Icon: ShieldCheck, t: "Garantía por escrito" }].map(({ Icon, t }) => (
                        <div key={t} className="flex items-center gap-3">
                            <Icon className="w-5 h-5 text-eclipse-cta flex-shrink-0" strokeWidth={1.75} />
                            <span className="text-white/90 text-xs sm:text-sm font-medium">{t}</span>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function StatsBarFlex() {
    const stats = [
        { value: "+20", label: "Años de experiencia" },
        { value: "+10,000", label: "Techos instalados" },
        { value: "+30", label: "Ciudades" },
        { value: "IFAI 2021", label: "Premio internacional" },
    ];
    return (
        <section data-testid="flexshade-stats" className="bg-eclipse-primary py-16 sm:py-20">
            <div className="container-eclipse">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-8">
                    {stats.map((s) => (
                        <div key={s.value} className="text-center sm:text-left">
                            <div className="font-display font-black text-eclipse-cta text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-2 leading-none">{s.value}</div>
                            <p className="text-white/75 text-xs sm:text-sm">{s.label}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function ModelsSection({ onSelectModel }) {
    return (
        <section id="modelos" data-testid="flexshade-models" className="bg-white py-20 sm:py-28">
            <div className="container-eclipse">
                <div className="max-w-3xl mb-14">
                    <p className="font-display font-bold text-xs tracking-[0.3em] text-eclipse-cta mb-3">5 SISTEMAS MODULARES</p>
                    <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-eclipse-primary tracking-tight leading-tight mb-4">Conoce nuestros 5 modelos</h2>
                    <p className="text-base sm:text-lg text-eclipse-muted">Diseñados para diferentes espacios y necesidades. Todos 100% atornillables, sin soldadura.</p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5">
                    {MODELS.map((m) => (
                        <article key={m.id} id={`modelo-${m.id}`} data-testid={`flexshade-model-${m.id}`} className="relative bg-white border border-neutral-200 hover:border-eclipse-cta transition-all rounded-sm overflow-hidden hover:shadow-xl flex flex-col">
                            {m.popular && (
                                <span className="absolute top-3 right-3 z-10 bg-eclipse-cta text-white font-display font-bold text-[10px] tracking-[0.15em] px-2 py-1">MÁS POPULAR</span>
                            )}
                            <div className="aspect-[4/3] bg-neutral-100 overflow-hidden">
                                <img src={m.img} alt={m.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                            </div>
                            <div className="p-5 flex-1 flex flex-col">
                                <h3 className="font-display font-bold text-lg text-eclipse-primary leading-tight">{m.title}</h3>
                                <p className="text-xs text-eclipse-cta font-display font-semibold mb-2">{m.subtitle}</p>
                                <p className="text-sm text-eclipse-muted mb-4 flex-1">{m.desc}</p>
                                <div className="flex flex-wrap gap-1.5 mb-4">
                                    {m.specs.map((s) => (
                                        <span key={s} className="text-[10px] font-medium bg-neutral-100 text-eclipse-primary px-2 py-1 rounded-sm">{s}</span>
                                    ))}
                                </div>
                                <button onClick={() => onSelectModel(m.title)} data-testid={`flexshade-model-${m.id}-cta`} className="btn-eclipse-outline-dark text-xs py-2.5 px-3 rounded-sm">
                                    Cotizar {m.title.replace("FlexShade ", "")}
                                </button>
                            </div>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}

function SpecsSection() {
    return (
        <section id="especificaciones" data-testid="flexshade-specs" className="bg-eclipse-light py-20 sm:py-28">
            <div className="container-eclipse">
                <div className="max-w-3xl mb-14">
                    <p className="font-display font-bold text-xs tracking-[0.3em] text-eclipse-cta mb-3">FICHA TÉCNICA</p>
                    <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-eclipse-primary tracking-tight leading-tight">Calidad y desempeño que dura</h2>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {SPECS.map(({ Icon, value, label }) => (
                        <div key={label} className="bg-white p-5 rounded-sm border border-neutral-200 hover:border-eclipse-cta transition-colors">
                            <Icon className="w-6 h-6 text-eclipse-cta mb-3" strokeWidth={1.5} />
                            <div className="font-display font-black text-xl text-eclipse-primary leading-tight">{value}</div>
                            <div className="text-xs text-eclipse-muted mt-1">{label}</div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

function NichesSection({ onSelectNiche }) {
    return (
        <section data-testid="flexshade-niches" className="bg-white py-20 sm:py-28">
            <div className="container-eclipse">
                <div className="max-w-3xl mb-14">
                    <p className="font-display font-bold text-xs tracking-[0.3em] text-eclipse-cta mb-3">APLICACIONES</p>
                    <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-eclipse-primary tracking-tight leading-tight">Aplicable en cualquier espacio</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {NICHES.map(({ Icon, title, desc, tipo }) => (
                        <button
                            key={title}
                            onClick={() => onSelectNiche(tipo)}
                            data-testid={`flexshade-niche-${title.toLowerCase().replace(/\s/g, "-").replace(/[^\w-]/g, "")}`}
                            className="group text-left bg-white border border-neutral-200 hover:border-eclipse-cta hover:-translate-y-1 hover:shadow-lg p-5 rounded-sm transition-all"
                        >
                            <div className="w-11 h-11 rounded-sm bg-eclipse-light group-hover:bg-eclipse-cta transition-colors flex items-center justify-center mb-3">
                                <Icon className="w-5 h-5 text-eclipse-primary group-hover:text-white transition-colors" strokeWidth={1.75} />
                            </div>
                            <h3 className="font-display font-bold text-base text-eclipse-primary mb-1 leading-tight">{title}</h3>
                            <p className="text-xs text-eclipse-muted leading-relaxed">{desc}</p>
                        </button>
                    ))}
                </div>
            </div>
        </section>
    );
}

function GallerySection() {
    const [filter, setFilter] = useState("Todos");
    const filters = ["Todos", "Estacionamientos", "Industrial", "Escuelas", "Comercial"];
    return (
        <section id="proyectos" data-testid="flexshade-gallery" className="bg-eclipse-light py-20 sm:py-28">
            <div className="container-eclipse">
                <div className="max-w-3xl mb-10">
                    <p className="font-display font-bold text-xs tracking-[0.3em] text-eclipse-cta mb-3">CASOS REALES</p>
                    <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-eclipse-primary tracking-tight leading-tight">Más de 10,000 techos instalados</h2>
                </div>
                <div className="flex flex-wrap gap-2 mb-8">
                    {filters.map((f) => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            className={`px-4 py-2 rounded-full text-xs font-display font-bold tracking-wider transition-colors ${filter === f ? "bg-eclipse-primary text-white" : "bg-white text-eclipse-primary border border-neutral-200 hover:border-eclipse-cta"}`}
                        >
                            {f.toUpperCase()}
                        </button>
                    ))}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                    {GALLERY.map((g) => (
                        <article key={g.title} className="group relative aspect-[4/3] overflow-hidden bg-eclipse-primary rounded-sm">
                            <img src={g.img} alt={g.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                            <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
                                <h3 className="font-display font-bold text-lg leading-tight">{g.title}</h3>
                                <p className="text-xs text-white/80">{g.city}</p>
                            </div>
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
}

function QuoteFormFlex({ initialTipo }) {
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        nombre: "",
        telefono: "",
        email: "",
        tipo_espacio: initialTipo || "",
        metros_cuadrados: "",
        ciudad: "",
        tiene_planos: "",
        mensaje: "",
    });

    useEffect(() => {
        if (initialTipo) setForm((f) => ({ ...f, tipo_espacio: initialTipo }));
    }, [initialTipo]);

    const set = (k) => (v) => setForm({ ...form, [k]: typeof v === "object" && v?.target ? v.target.value : v });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.nombre.trim() || form.nombre.trim().length < 2) return toast.error("Ingresa tu nombre completo");
        if (!form.telefono.trim() || form.telefono.replace(/\D/g, "").length < 10) return toast.error("Ingresa un teléfono válido (10 dígitos)");
        if (!form.tipo_espacio) return toast.error("Selecciona el tipo de espacio");
        if (!form.metros_cuadrados) return toast.error("Selecciona los metros cuadrados");
        if (!form.ciudad) return toast.error("Selecciona la ciudad");
        if (!form.tiene_planos) return toast.error("Indica si tienes planos");

        setSubmitting(true);
        try {
            await submitLead({ ...form, sitio: "flexshade", producto: "flexshade", fuente: "cotizacion" });
            toast.success("¡Listo! Te contactamos en menos de 24h.");
            navigate("/gracias?sitio=flexshade");
        } catch (err) {
            toast.error("Hubo un problema. Intenta de nuevo o escríbenos al WhatsApp.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section id="cotizar" data-testid="flexshade-quote-section" className="bg-eclipse-light py-20 sm:py-28">
            <div className="container-eclipse">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                    <div>
                        <p className="font-display font-bold text-xs tracking-[0.3em] text-eclipse-cta mb-3">COTIZACIÓN GRATUITA</p>
                        <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-eclipse-primary tracking-tight leading-tight mb-4">Cotiza tu techo FlexShade gratis</h2>
                        <p className="text-base sm:text-lg text-eclipse-muted mb-8 leading-relaxed">Te contactamos en menos de 24 horas con propuesta personalizada.</p>
                        <ul className="space-y-3 mb-8">
                            {["Cotización sin costo", "Visita técnica gratis", "20 años de garantía"].map((b) => (
                                <li key={b} className="flex items-center gap-3 text-eclipse-primary font-medium">
                                    <CheckCircle2 className="w-5 h-5 text-eclipse-cta flex-shrink-0" />
                                    {b}
                                </li>
                            ))}
                        </ul>
                        <a href={`tel:${TEL_RAW}`} data-testid="flexshade-form-phone" className="inline-flex items-center gap-3 font-display font-black text-2xl text-eclipse-primary hover:text-eclipse-cta transition-colors">
                            <Phone className="w-6 h-6 text-eclipse-cta" />
                            {TEL}
                        </a>
                    </div>
                    <form onSubmit={submit} data-testid="flexshade-quote-form" className="bg-white p-6 sm:p-8 rounded-sm shadow-xl border border-neutral-100">
                        <div className="space-y-4">
                            <Field label="Nombre completo *">
                                <Input value={form.nombre} onChange={set("nombre")} data-testid="flexshade-form-nombre" required />
                            </Field>
                            <Field label="Teléfono / WhatsApp *">
                                <Input type="tel" value={form.telefono} onChange={set("telefono")} placeholder="+52 81 1234 5678" data-testid="flexshade-form-telefono" required />
                            </Field>
                            <Field label="Email">
                                <Input type="email" value={form.email} onChange={set("email")} data-testid="flexshade-form-email" />
                            </Field>
                            <Field label="Tipo de espacio *">
                                <Select value={form.tipo_espacio} onValueChange={set("tipo_espacio")}>
                                    <SelectTrigger data-testid="flexshade-form-tipo-espacio"><SelectValue placeholder="Selecciona..." /></SelectTrigger>
                                    <SelectContent>
                                        {TIPOS_ESPACIO.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </Field>
                            <Field label="Metros cuadrados aproximados *">
                                <Select value={form.metros_cuadrados} onValueChange={set("metros_cuadrados")}>
                                    <SelectTrigger data-testid="flexshade-form-m2"><SelectValue placeholder="Selecciona..." /></SelectTrigger>
                                    <SelectContent>
                                        {M2_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </Field>
                            <Field label="Ciudad *">
                                <Select value={form.ciudad} onValueChange={set("ciudad")}>
                                    <SelectTrigger data-testid="flexshade-form-ciudad"><SelectValue placeholder="Selecciona..." /></SelectTrigger>
                                    <SelectContent>
                                        {CIUDADES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </Field>
                            <Field label="¿Tienes planos del espacio? *">
                                <RadioGroup value={form.tiene_planos} onValueChange={set("tiene_planos")} className="flex flex-wrap gap-4 pt-1">
                                    {[{ v: "si", l: "Sí" }, { v: "en_proceso", l: "En proceso" }, { v: "no", l: "No" }].map((o) => (
                                        <Label key={o.v} className="flex items-center gap-2 cursor-pointer font-normal text-sm">
                                            <RadioGroupItem value={o.v} data-testid={`flexshade-form-planos-${o.v}`} />
                                            {o.l}
                                        </Label>
                                    ))}
                                </RadioGroup>
                            </Field>
                            <Field label="Mensaje (opcional)">
                                <Textarea value={form.mensaje} onChange={set("mensaje")} placeholder="Describe tu espacio o proyecto (opcional)" rows={3} data-testid="flexshade-form-mensaje" />
                            </Field>
                            <Button type="submit" disabled={submitting} data-testid="flexshade-form-submit" className="w-full btn-eclipse-cta py-6 text-sm uppercase tracking-wider rounded-sm">
                                {submitting ? "Enviando..." : "Quiero mi cotización gratis →"}
                            </Button>
                            <p className="text-[11px] text-eclipse-muted text-center leading-relaxed">Al enviar aceptas nuestro aviso de privacidad. Nos pondremos en contacto en menos de 24 horas.</p>
                        </div>
                    </form>
                </div>
            </div>
        </section>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <Label className="text-xs font-display font-semibold text-eclipse-primary tracking-wider uppercase mb-1.5 block">{label}</Label>
            {children}
        </div>
    );
}

function MaterialsColors() {
    return (
        <section id="materiales" data-testid="flexshade-materials" className="bg-white py-20 sm:py-28">
            <div className="container-eclipse">
                <div className="max-w-3xl mb-12">
                    <p className="font-display font-bold text-xs tracking-[0.3em] text-eclipse-cta mb-3">MATERIALES</p>
                    <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-eclipse-primary tracking-tight leading-tight">Materiales premium importados</h2>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <article className="bg-eclipse-light p-7 rounded-sm">
                        <img src="https://static.prod-images.emergentagent.com/jobs/48bc66d4-e137-48bf-811d-bee5c078c182/images/001fcedc0c6d288cecf008efda0240c5971c4bebc2ba48bbd5cd226b94cc66ae.jpeg" alt="Malla sombra de alta densidad" className="w-full aspect-[16/10] object-cover mb-6 rounded-sm" />
                        <h3 className="font-display font-bold text-2xl text-eclipse-primary mb-2">Malla sombra de alta densidad</h3>
                        <p className="text-sm text-eclipse-muted mb-4">Marcas importadas premium — las mismas que usa Skycover en proyectos arquitectónicos.</p>
                        <div className="flex flex-wrap gap-2 mb-5" data-testid="flexshade-membrane-brands">
                            {MEMBRANE_BRANDS.map((b) => (
                                <span key={b} className="inline-flex items-center px-3 py-1.5 bg-white border border-neutral-200 rounded-sm text-xs font-display font-semibold text-eclipse-primary tracking-wide">
                                    {b}
                                </span>
                            ))}
                        </div>
                        <ul className="space-y-2">
                            {["95% bloqueo UV", "Tratamiento anti-hongos y anti-UV", "Resistencia al viento hasta 70 km/hr", "Vida útil hasta 20 años"].map((b) => (
                                <li key={b} className="flex items-center gap-2 text-sm text-eclipse-primary">
                                    <Check className="w-4 h-4 text-eclipse-cta" />
                                    {b}
                                </li>
                            ))}
                        </ul>
                    </article>
                    <article className="bg-eclipse-light p-7 rounded-sm">
                        <h3 className="font-display font-bold text-2xl text-eclipse-primary mb-2" data-testid="flexshade-colors-title">5 colores de línea disponibles</h3>
                        <p className="text-sm text-eclipse-muted mb-5">Colores de línea regular. Otros colores y acabados disponibles bajo pedido.</p>
                        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3" data-testid="flexshade-colors-grid">
                            {COLORS.map((c) => (
                                <div key={c.name} className="group flex flex-col items-center text-center">
                                    <div
                                        className="w-full aspect-square rounded-sm border border-neutral-300 shadow-sm group-hover:scale-105 transition-transform"
                                        style={{ backgroundColor: c.hex }}
                                        title={`${c.name} (${c.hex})`}
                                        data-testid={`flexshade-color-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                                    />
                                    <span className="mt-1.5 text-[10px] font-display font-semibold text-eclipse-primary leading-tight group-hover:text-eclipse-cta transition-colors">
                                        {c.name}
                                    </span>
                                </div>
                            ))}
                        </div>
                        <p className="text-[11px] text-eclipse-muted mt-4 italic">Colores sujetos a disponibilidad. Otros colores especiales bajo cotización.</p>
                    </article>
                </div>
            </div>
        </section>
    );
}

function CatalogSection() {
    const [submitting, setSubmitting] = useState(false);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!name.trim() || !email.trim()) return toast.error("Completa nombre y email");
        setSubmitting(true);
        try {
            const res = await submitCatalogLead({ nombre: name, email, sitio: "flexshade" });
            toast.success("¡Listo! Te enviamos también el catálogo a tu correo.");
            window.open(res.catalog_url || "/catalogo_flexshade.pdf", "_blank");
        } catch (err) {
            toast.error("No pudimos generar el catálogo. Intenta de nuevo.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section id="catalogo" data-testid="flexshade-catalog" className="bg-eclipse-primary py-20 sm:py-24">
            <div className="container-eclipse">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                    <img src="https://static.prod-images.emergentagent.com/jobs/48bc66d4-e137-48bf-811d-bee5c078c182/images/08d43fbcddcb9895d6ab99fda58d547d5df6ab36006f10712417c9e24ce974a6.jpeg" alt="Portada del catálogo FlexShade" className="w-full aspect-[4/3] object-cover rounded-sm shadow-2xl" />
                    <div className="text-white">
                        <p className="font-display font-bold text-xs tracking-[0.3em] text-eclipse-cta mb-3">CATÁLOGO PDF</p>
                        <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight mb-4">Descarga el catálogo FlexShade</h2>
                        <p className="text-white/80 text-base sm:text-lg mb-8 leading-relaxed">Especificaciones técnicas, modelos, casos de éxito.</p>
                        <form onSubmit={onSubmit} className="space-y-3 max-w-md">
                            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Tu nombre" data-testid="flexshade-catalog-nombre" className="bg-white/10 border-white/20 text-white placeholder:text-white/50" required />
                            <Input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Tu email" data-testid="flexshade-catalog-email" className="bg-white/10 border-white/20 text-white placeholder:text-white/50" required />
                            <Button type="submit" disabled={submitting} data-testid="flexshade-catalog-submit" className="w-full btn-eclipse-cta py-5 text-sm uppercase tracking-wider rounded-sm inline-flex items-center justify-center gap-2">
                                <Download className="w-4 h-4" />
                                {submitting ? "Generando..." : "Descargar PDF gratis"}
                            </Button>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
}

function FaqSection() {
    return (
        <section id="faq" data-testid="flexshade-faq" className="bg-white py-20 sm:py-28">
            <div className="container-eclipse max-w-4xl">
                <div className="mb-12">
                    <p className="font-display font-bold text-xs tracking-[0.3em] text-eclipse-cta mb-3">DUDAS COMUNES</p>
                    <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-eclipse-primary tracking-tight leading-tight">Preguntas frecuentes</h2>
                </div>
                <Accordion type="single" collapsible className="space-y-3">
                    {FAQS.map((f, i) => (
                        <AccordionItem key={i} value={`item-${i}`} data-testid={`flexshade-faq-${i}`} className="border border-neutral-200 rounded-sm px-5">
                            <AccordionTrigger className="font-display font-bold text-base text-eclipse-primary hover:no-underline hover:text-eclipse-cta text-left">{f.q}</AccordionTrigger>
                            <AccordionContent className="text-sm text-eclipse-muted leading-relaxed">{f.a}</AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </div>
        </section>
    );
}

function FinalCTA({ onCotizar }) {
    return (
        <section data-testid="flexshade-final-cta" className="bg-eclipse-primary py-20 sm:py-28">
            <div className="container-eclipse text-center">
                <p className="font-display font-bold text-xs tracking-[0.3em] text-eclipse-cta mb-5">EMPECEMOS HOY</p>
                <h2 className="font-display font-black text-white text-3xl sm:text-5xl lg:text-6xl tracking-tight leading-tight mb-5">Empieza tu proyecto hoy</h2>
                <p className="text-white/85 text-base sm:text-lg max-w-2xl mx-auto mb-10">Cotización gratuita en menos de 24 horas</p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
                    <a href={WA_HERO} target="_blank" rel="noopener noreferrer" data-testid="flexshade-final-whatsapp" className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-sm font-display font-bold text-sm uppercase tracking-wider bg-[#25D366] hover:bg-[#1ebd5b] text-white transition-all">
                        <MessageCircle className="w-5 h-5" />
                        Cotizar por WhatsApp
                    </a>
                    <button onClick={onCotizar} data-testid="flexshade-final-cta-btn" className="btn-eclipse-cta px-7 py-4 rounded-sm text-sm tracking-wider uppercase">Cotizar en línea</button>
                </div>
                <a href={`tel:${TEL_RAW}`} className="inline-flex items-center gap-3 font-display font-black text-2xl text-white hover:text-eclipse-cta transition-colors">
                    <Phone className="w-6 h-6 text-eclipse-cta" />
                    {TEL}
                </a>
                <p className="text-white/60 text-sm mt-2">Lun-Vie 9-18h</p>
            </div>
        </section>
    );
}

// ---------- Página principal ----------
export default function Flexshade() {
    const [initialTipo, setInitialTipo] = useState("");

    useEffect(() => {
        applySEO({
            path: "/flexshade",
            title: "FlexShade® | Techos de Malla Sombra Modulares para Estacionamientos | Eclipse",
            description: "Sistema modular prefabricado de malla sombra. Sin permiso, sin soldadura. Instalación en días. 5 modelos: Walk, Curve, Sun, Curve Corto, Back to Back. Cotización gratis.",
            keywords: "techos malla sombra, toldos estacionamiento, FlexShade modular, cubierta prefabricada, malla sombra Monterrey, techos modulares México",
            ogImage: "/og/og-flexshade.png",
            ogImageAlt: "FlexShade — Techos modulares de malla sombra para estacionamientos",
            jsonLD: [
                orgSchema,
                localBusinessSchema,
                breadcrumbSchema([{ name: "Inicio", path: "/" }, { name: "FlexShade", path: "/flexshade" }]),
                productSchema({ name: "FlexShade — Techos modulares de malla sombra", description: "Sistema modular prefabricado de malla sombra para estacionamientos y áreas comerciales. 5 modelos. 10 años de garantía, vida útil 20 años.", image: HERO_IMG, sku: "FLEXSHADE-MODULAR" }),
                videoObjectSchema({
                    name: videos.flexshade_feature[0].title,
                    description: "FlexShade — sistema modular prefabricado de techos de malla sombra para estacionamientos. Sin permiso, sin soldadura.",
                    videoId: videos.flexshade_feature[0].videoId,
                }),
                faqSchema(FAQS),
            ],
        });
        trackEvent({ event_type: "page_view", path: "/flexshade", sitio: "flexshade" });
    }, []);

    const scrollToCotizar = () => {
        const el = document.getElementById("cotizar");
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const handleModelCta = (modelName) => {
        setInitialTipo("Estacionamiento");
        toast.info(`${modelName} preseleccionado en la cotización`);
        setTimeout(scrollToCotizar, 100);
    };

    const handleNicheCta = (tipo) => {
        setInitialTipo(tipo);
        setTimeout(scrollToCotizar, 100);
    };

    return (
        <div className="min-h-screen bg-white theme-flexshade" data-testid="flexshade-page">
            <NavbarFlex onCotizar={scrollToCotizar} />
            <main>
                <HeroFlex onCotizar={scrollToCotizar} />
                <Breadcrumbs items={[{ label: "FlexShade" }]} testId="flexshade-breadcrumbs" />
                <StatsBarFlex />
                <InlineCTABanner heading="¿Tu estacionamiento expuesto al sol?" subheading="Cotiza tu techo FlexShade modular. Respuesta en 24h." whatsappUrl={WA_HERO} tel={TEL_RAW} onCtaClick={scrollToCotizar} ctaLabel="Cotizar ahora" variant="light" testId="flex-banner-1" />
                <ModelsSection onSelectModel={handleModelCta} />
                <VideoSection
                    eyebrow="FlexShade en acción"
                    title="Mira el sistema FlexShade en obra"
                    subtitle="Instalación rápida, sin permiso ni soldadura"
                    videoId={videos.flexshade_feature[0].videoId}
                    videoTitle={videos.flexshade_feature[0].title}
                    isShort={videos.flexshade_feature[0].isShort}
                    bullets={[
                        "Sistema 100% atornillable, sin soldadura",
                        "Sin permiso de construcción",
                        "Instalación en días, no semanas",
                        "Vida útil 20 años con malla importada",
                    ]}
                    primaryCtaLabel="Cotizar mi FlexShade"
                    onPrimaryCta={scrollToCotizar}
                    sectionId="flexshade-video"
                    testId="flex-video-section"
                />
                <YouTubeShortsRow
                    shorts={videos.shorts}
                    title="Mas videos del canal"
                    subtitle="Shorts de techos modulares e instalaciones Eclipse"
                    testId="flex-shorts-row"
                />
                <SpecsSection />
                <NichesSection onSelectNiche={handleNicheCta} />
                <GallerySection />
                <InlineCTABanner heading="¿Tienes planos o medidas?" subheading="Cotización con visita técnica gratis. Cobertura nacional." whatsappUrl={WA_HERO} tel={TEL_RAW} onCtaClick={scrollToCotizar} ctaLabel="Cotizar gratis" variant="dark" testId="flex-banner-2" />
                <QuoteFormFlex initialTipo={initialTipo} />
                <MaterialsColors />
                <CatalogSection />
                <FaqSection />
                <p className="text-center text-sm text-eclipse-muted py-8 px-4">¿Buscas tensoestructuras premium a la medida? Visita <a href="/velarias" className="text-eclipse-cta font-display font-bold underline">Skycover</a> · Sitio madre <a href="/" className="text-eclipse-primary font-display font-bold underline">Eclipse Cubiertas</a></p>
                <FinalCTA onCotizar={scrollToCotizar} />
            </main>
            <FloatingWhatsApp phone={WA_RAW} message={WA_FLOATING_MSG} testId="flexshade-floating-wa" />
            <StickyMobileCTABar whatsappUrl={WA_HERO} ctaHref={`tel:${TEL_RAW}`} ctaLabel="Llamar" testId="flex-sticky-cta" />
            <BackToTop testId="flex-back-to-top" />
        </div>
    );
}
